import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { trackConversion, trackEngagement, getCurrentVariant } from '../../utils/abTestTracking';
import { trackABTestEvent } from '../../services/abTestService';

interface TrackingContextType {
  trackConversion: (eventName: string, additionalData?: Record<string, any>) => void;
  trackEngagement: (eventName: string, additionalData?: Record<string, any>) => void;
  currentVariant: string | null;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

interface TrackingProviderProps {
  children: ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const [currentVariant, setCurrentVariant] = useState<string | null>(null);

  useEffect(() => {
    // Get the current variant from localStorage
    const variant = getCurrentVariant();
    setCurrentVariant(variant);
  }, []);

  const handleTrackConversion = (eventName: string, additionalData: Record<string, any> = {}) => {
    // Track in local utility
    trackConversion(eventName, additionalData);
    
    // Track in API service if a variant is set
    if (currentVariant) {
      trackABTestEvent('conversion', eventName, currentVariant, additionalData)
        .catch(error => console.error('Failed to track conversion event:', error));
    }
  };

  const handleTrackEngagement = (eventName: string, additionalData: Record<string, any> = {}) => {
    // Track in local utility
    trackEngagement(eventName, additionalData);
    
    // Track in API service if a variant is set
    if (currentVariant) {
      trackABTestEvent('engagement', eventName, currentVariant, additionalData)
        .catch(error => console.error('Failed to track engagement event:', error));
    }
  };

  const value = {
    trackConversion: handleTrackConversion,
    trackEngagement: handleTrackEngagement,
    currentVariant,
  };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = (): TrackingContextType => {
  const context = useContext(TrackingContext);
  if (context === undefined) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
};

// Utility hook to track page views automatically
export const usePageView = (pageName: string, additionalData: Record<string, any> = {}) => {
  const { trackEngagement } = useTracking();
  
  useEffect(() => {
    trackEngagement('page_view', { page: pageName, ...additionalData });
    
    // Track scroll depth
    const handleScroll = () => {
      const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      
      if (scrollDepth === 25 || scrollDepth === 50 || scrollDepth === 75 || scrollDepth === 100) {
        trackEngagement('scroll_depth', { depth: scrollDepth, page: pageName });
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackEngagement, pageName, additionalData]);
}; 